<template>
  <!-- header -->
  <div class="position-fixed top-0 start-50 translate-middle-x w-100 mw-640 z-5 pb-1 bg-white">
    <div v-if="!check_if_client()" class="h-header w-100 d-flex align-items-center ps-4 pe-3">
      <header class="flex-grow-1 fs-6 text-muted d-flex align-items-center">
        <b>分析報告</b>
      </header>
    </div>
    <!-- date picker -->
    <div class="h-header w-100 d-flex px-3">
      <div class="w-100">
        <VueDatePicker v-model="date" placeholder="選擇日期範圍" range :enable-time-picker="false" :format="'yyyy/MM/dd'">
          <template #clear-icon="{ clear }">
            <span @click="clear" class="close-icon pe-1 text-black-30"><svg width="20" height="20"><use xlink:href="#close-circle"/></svg></span>
          </template>
        </VueDatePicker>
      </div>
    </div>
  </div>

  <!-- main -->
  <main class="d-flex flex-column w-100 flex-1"
  :class="{'hideTitle': check_if_client()}">
  
    <!-- loading spinner -->
    <div v-if="isLoading" class="absoluteFlexCenter">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- no data -->
    <div v-if="!isLoading && orders.length === 0" class="flex-1 p-4 flexCenter">
      <div class="w-100 text-center">
        <h6 class="text-secondary opacity-75">尚無資料可分析</h6>
        <small>請至訂單管理新增或匯入資訊。</small>
      </div>
    </div>

    <!-- filtered no data -->
    <div v-else-if="!isLoading && filteredOrders.length === 0" class="flex-1 p-4 flexCenter">
      <div class="w-100 text-center">
        <h6 class="text-muted">沒有符合的資料</h6>
        <small>請確認關鍵字或日期範圍並再試一次。</small>
      </div>
    </div>
    
    <!-- statistics -->
    <div v-else-if="!isLoading && filteredOrders.length > 0">

      <!-- data -->
      <ul class="list-group list-group-flush mb-3">
        <li v-for="n in statistics" :key="n" class="list-group-item py-0">
          <div class="p-3 border-bottom d-flex justify-content-center align-items-center">
            <div class="me-4">
              {{n.title}}
              <span
                v-if="n.title === '累計自用BV點數' || n.title === '累計自用IBV點數' || n.title === '累計自用銷售金額'"
                class="d-inline-flex align-items-center"
              >
                <span class="beta-badge me-1">BETA</span>
                <span class="info-icon" @click="showEstimateInfo">?</span>
              </span>
            </div>
            <span v-if="n.data === 0" class="fs-5.5">{{n.data}}</span>
            <div v-else>
              <span v-if="n.title === '未付款訂單數量' || n.title === '未到貨訂單數量'"
                class="badge rounded-pill fs-6 danger-mix-black-20 bg-danger-subtle">{{n.data}}</span>
              <span v-else-if="n.title === '累計BV零售利潤' || n.title === '累計銷售總額'"
                class="fw-bolder primary-mix-black-30">${{n.data}}</span>
              <span v-else-if="n.title === '累計訂單數量'"
                class="badge rounded-pill fs-6 success-mix-black-20 bg-success-subtle">{{n.data}}</span>
              <span v-else class="badge rounded-pill fs-6 primary-mix-black-30 bg-primary-subtle">{{n.data}}</span>
            </div>
          </div>
        </li>
      </ul>

      <!-- chart -->
      <div v-if="topThreeVIPs.labels.length !== 0" class="m-3">
        <h6 class="text-center pt-3 mb-3">VIP客戶前三名</h6>
        <div class="pie-chart">
          <Doughnut :data="topThreeVIPs" :options="options"/>
        </div>
        
        <div class="py-3 mw-20rem mx-auto text-center">
          <div v-for="(label, index) in topThreeVIPs.labels" :key="label"
          class="small overflow-hidden d-flex justify-content-between align-items-center mb-1">
            <div class="text-truncate">
              <svg class="mb-1 me-2 ranking-dot" xmlns="http://www.w3.org/2000/svg">
                <circle cx="50%" cy="50%" r="50%" :fill="getCircleColor(index)" />
              </svg>{{ label }}
            </div>
            <div class="fs-6 ms-2">${{ topThreeVIPs.datasets[0].data[index] }}</div>
          </div>
        </div>
      </div>
      <div v-if="mostPopularIBVItems.labels.length !== 0" class="m-3">
        <div class="border-top mb-3"></div>
        <h6 class="text-center pt-3 mb-3">最受歡迎的 IBV 商品前三名</h6>
        <div class="pie-chart">
          <Doughnut :data="mostPopularIBVItems" :options="options"/>
        </div>
        
        <div class="py-3 mw-20rem mx-auto text-center">
          <div v-for="(label, index) in mostPopularIBVItems.labels" :key="label"
          class="small overflow-hidden d-flex justify-content-between align-items-center mb-1">
            <div class="text-truncate text-start">
              <svg class="mb-1 me-2 ranking-dot" xmlns="http://www.w3.org/2000/svg">
                <circle cx="50%" cy="50%" r="50%" :fill="getCircleColor(index)" />
              </svg>{{ label }}
            </div>
            <div class="fs-6 ms-2">{{ mostPopularIBVItems.datasets[0].data[index] }}</div>
          </div>
        </div>
      </div>
      <div v-if="mostPopularBVItems.labels.length !== 0" class="m-3">
        <div class="border-top mb-3"></div>
        <h6 class="text-center pt-3 mb-3">最受歡迎的 BV 商品前三名</h6>
        <div class="pie-chart">
          <Doughnut :data="mostPopularBVItems" :options="options"/>
        </div>
        
        <div class="py-3 mw-20rem mx-auto text-center">
          <div v-for="(label, index) in mostPopularBVItems.labels" :key="label"
          class="small overflow-hidden d-flex justify-content-between align-items-center mb-1">
            <div class="text-truncate text-start">
              <svg class="mb-1 me-2 ranking-dot" xmlns="http://www.w3.org/2000/svg">
                <circle cx="50%" cy="50%" r="50%" :fill="getCircleColor(index)" />
              </svg>{{ label }}
            </div>
            <div class="fs-6 ms-2">{{ mostPopularBVItems.datasets[0].data[index] }}</div>
          </div>
        </div>
      </div>

    </div>
  </main>
  </template>
  
  <script>
  import _ from 'lodash';
  import dayjs from 'dayjs';
  import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
  import { Doughnut } from 'vue-chartjs';
  import VueDatePicker from '@vuepic/vue-datepicker';
  import '@vuepic/vue-datepicker/dist/main.css';
  import liff from '@line/liff';
  import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
  import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
  import axios from 'axios';
  import { mapGetters } from 'vuex';
  import { showModal, cleanupModals } from '@/utils/modal'

  let api_url = process.env.VUE_APP_API_URL;

  dayjs.extend(isSameOrBefore);
  dayjs.extend(isSameOrAfter);
  
  ChartJS.register(ArcElement, Tooltip, Legend)

  export default {
    name: 'StatisticsCom',
    props: {
      mainDate: {
        type: Array,
        default: () => []
      }
    },
    components: {
      Doughnut,
      VueDatePicker
    },
    computed: {
      ...mapGetters(['token', 'userId', 'name']),

      filteredOrders() {
        if (!this.date || this.date.length === 0) {
          return this.orders;
        }

        const [startDate, endDate] = this.date.map(date => dayjs(date));
        console.log(startDate, endDate);
        return this.orders.filter(order => {
          const orderDate = dayjs(order.Date, 'YYYY/MM/DD');
          return orderDate.isSameOrAfter(startDate.startOf('day')) && orderDate.isSameOrBefore(endDate.startOf('day'));
        });
      },
      unpaidOrders() {
        return _.filter(this.filteredOrders, order => {
          return order.Paid === 'No' || order.Paid === '' || order.Paid === null;
        });
      },
      totalSales() {
        return _.chain(this.filteredOrders)
          .sumBy('Total')
          .value();
      },
      ordersNotArrived() {
        return _.filter(this.filteredOrders, order => {
          return order.Arrived === 'No' || order.Arrived === '' || order.Arrived === null;
        });
      },
      topThreeVIPs() {
        if (!Array.isArray(this.filteredOrders)) {
          return {
            labels: [],
            datasets: [{
              data: []
            }]
          };
        }

        const groupedCustomers = _.chain(this.filteredOrders)
          .filter(order => {
            const customerName = order.Customer?.toLowerCase() || '';
            const personalKeywords = ['自己', '我'];
            return !personalKeywords.some(keyword => customerName.includes(keyword));
          })
          .groupBy('Customer')
          .map((orders, name) => ({
            Customer: name,
            TotalAmount: _.sumBy(orders, 'Total'),
            OrdersCount: orders.length
          }))
          .orderBy(['TotalAmount', 'OrdersCount'], ['desc', 'desc'])
          .slice(0, 3)
          .value();

        return {
          labels: groupedCustomers.map(customer => customer.Customer),
          datasets: [{
            backgroundColor: ['#00a8ca', '#ffba4a', '#46BD74'],
            hoverBackgroundColor: ['#00C3E6', '#FFD537', '#55CE53'],
            hoverBorderWidth: 0,
            hoverBorderColor: ['white', 'white', 'white'],
            data: groupedCustomers.map(customer => customer.TotalAmount)
          }]
        };
      },
      ordersAmount() {
        return this.filteredOrders.length;
      },

      personalBVPercentage() {
        const totalBV = this.totalIBVandBVandBVProfit.BV;
        const personalBV = this.personalBVPoints;
        if (!totalBV || !personalBV) return 0;
        return ((personalBV / totalBV) * 100).toFixed(1);
      },

      personalIBVPercentage() {
        const totalIBV = this.totalIBVandBVandBVProfit.IBV;
        const personalIBV = this.personalIBVPoints;
        if (!totalIBV || !personalIBV) return 0;
        return ((personalIBV / totalIBV) * 100).toFixed(1);
      },

      personalTotalSalesWithPercentage() {
        if (!Array.isArray(this.filteredOrders)) {
          return 0;
        }
        
        const personalSales = _.chain(this.filteredOrders)
          .filter(order => {
            const customerName = order.Customer?.toLowerCase() || '';
            const personalKeywords = ['自己', '我'];
            return personalKeywords.some(keyword => 
              customerName.includes(keyword)
            );
          })
          .sumBy('Total')
          .value();

        const percentage = this.totalSales ? ((personalSales / this.totalSales) * 100).toFixed(1) : 0;
        
        return personalSales ? `${parseFloat(personalSales.toFixed(2))} (${percentage}%)` : '';
      },

      personalBVPoints() {
        if (!Array.isArray(this.filteredOrders)) {
          return 0;
        }
        
        return _.chain(this.filteredOrders)
          .filter(order => {
            const customerName = order.Customer?.toLowerCase() || '';
            
            // Check for personal indicators or name parts
            const personalKeywords = ['自己', '我'];
            const hasPersonalKeyword = personalKeywords.some(keyword => 
              customerName.includes(keyword)
            );
            
            return hasPersonalKeyword;
          })
          .sumBy('BV')
          .value();
      },

      personalIBVPoints() {
        if (!Array.isArray(this.filteredOrders)) {
          return 0;
        }

        return _.chain(this.filteredOrders)
          .filter(order => {
            const customerName = order.Customer?.toLowerCase() || '';

            // Check for personal indicators or name parts
            const personalKeywords = ['自己', '我'];
            const hasPersonalKeyword = personalKeywords.some(keyword =>
              customerName.includes(keyword)
            );

            return hasPersonalKeyword;
          })
          .sumBy('IBV')
          .value();
      },

      personalTotalSales() {
        if (!Array.isArray(this.filteredOrders)) {
          return 0;
        }
        
        return _.chain(this.filteredOrders)
          .filter(order => {
            const customerName = order.Customer?.toLowerCase() || '';
            const personalKeywords = ['自己', '我'];
            return personalKeywords.some(keyword => 
              customerName.includes(keyword)
            );
          })
          .sumBy('Total')
          .value();
      },

      mostPopularBVItems() {
          
        if (!Array.isArray(this.filteredOrders)) {
          return {
            labels: [],
            datasets: [{
              data: []
            }]
          };
        }

        const groupedItems = _.chain(this.filteredOrders)
          .filter(order => order.BV !== null && order.BV !== '' && order.BV !== 0)
          .groupBy('Item')
          .map((items, name) => ({
            Item: name,
            Quantity: items.length
          }))
          .orderBy('Quantity', 'desc')
          .take(3) // 獲取前三名
          .value();

        return {
          labels: groupedItems.map(item => item.Item),
          datasets: [{
            backgroundColor: ['#00a8ca', '#ffba4a', '#46BD74'],
            hoverBackgroundColor: ['#00C3E6', '#FFD537', '#55CE53'],
            hoverBorderWidth: 0,
            hoverBorderColor: ['white', 'white', 'white'],
            data: groupedItems.map(item => item.Quantity)
          }]
        };
      },

      mostPopularIBVItems() {

        if (!Array.isArray(this.filteredOrders)) {
          return {
            labels: [],
            datasets: [{
              data: []
            }]
          };
        }

        const groupedItems = _.chain(this.filteredOrders)
          .filter(order => order.IBV !== null && order.IBV !== '' && order.IBV !== 0)
          .groupBy('Item')
          .map((items, name) => ({
            Item: name,
            Quantity: items.length
          }))
          .orderBy('Quantity', 'desc')
          .take(3) // 獲取前三名
          .value();

        return {
          labels: groupedItems.map(item => item.Item),
          datasets: [{
            backgroundColor: ['#00a8ca', '#ffba4a', '#46BD74'],
            hoverBackgroundColor: ['#00C3E6', '#FFD537', '#55CE53'],
            hoverBorderWidth: 0,
            hoverBorderColor: ['white', 'white', 'white'],
            data: groupedItems.map(item => item.Quantity)
          }]
        };
      },

      totalIBVandBVandBVProfit() {
        return _.reduce(this.filteredOrders, (acc, item) => {
          acc.IBV += item.IBV || 0;
          acc.BV += item.BV || 0;
          acc.BVProfit += item.BVProfit || 0;
          return acc;
        }, { IBV: 0, BV: 0, BVProfit: 0 });
      },
      statistics() {
        return [
          {
            title: '累計銷售總額',
            data: this.totalSales
          },
          {
            title: '累計零售利潤',
            data: this.totalIBVandBVandBVProfit ? parseFloat(this.totalIBVandBVandBVProfit.BVProfit.toFixed(2)) : ''
          },
          {
            title: '累計自用銷售金額',
            data: this.personalTotalSalesWithPercentage == '' ? 0 : this.personalTotalSalesWithPercentage
          },
          {
            title: '累計IBV點數',
            data: this.totalIBVandBVandBVProfit ? parseFloat(this.totalIBVandBVandBVProfit.IBV.toFixed(2)) : ''
          },
          {
            title: '累計自用IBV點數',
            data: this.personalIBVPoints ? `${parseFloat(this.personalIBVPoints.toFixed(2))} (${this.personalIBVPercentage}%)` : 0
          },
          {
            title: '累計BV點數',
            data: this.totalIBVandBVandBVProfit ? parseFloat(this.totalIBVandBVandBVProfit.BV.toFixed(2)) : ''
          },
          {
            title: '累計自用BV點數',
            data: this.personalBVPoints ? `${parseFloat(this.personalBVPoints.toFixed(2))} (${this.personalBVPercentage}%)` : 0
          },
          {
            title: '累計訂單數量',
            data: this.ordersAmount
          },
          {
            title: '未付款訂單數量',
            data: this.unpaidOrders ? this.unpaidOrders.length : ''
          },
          {
            title: '未到貨訂單數量',
            data: this.ordersNotArrived ? this.ordersNotArrived.length : ''
          }
        ]
      }
    },
    data() {
      return {
        orders: [],
        isLoading: false,
        date: this.mainDate,
        options: {
          responsive: true,
          plugins: {
            legend: {
              display: false
            }
          }
        }
      }
    },
    async mounted() {
      await this.fetchAllOrders();
    },
    unmounted() {
      cleanupModals();
    },
    methods: {
      getCircleColor(index) {
        const colors = ["#00a8ca", "#ffba4a", "#46BD74"];
        return colors[index] || "#000"; // 默认颜色为黑色
      },
      check_if_client() {
        return liff.isInClient()
      },
      async fetchAllOrders() {
        this.isLoading = true;
        try {
          const response = await axios.get(`${api_url}/v1/orders/${this.userId}`, {
            params: {
              offset: 0,
              limit: 1000000  // A very large number to effectively remove the limit
            },
            headers: {
              'Authorization': `Bearer ${this.token}`,
              "ngrok-skip-browser-warning": "69420"
            }
          });
          this.orders = response.data;
        } catch (error) {
          console.error('Error fetching orders:', error);
        } finally {
          this.isLoading = false;
        }
      },
      showEstimateInfo() {
        showModal('系統會自動檢查訂單中的「客戶姓名」欄位是否包含「自己」、「我」，估算自用訂單銷售金額以及IBV/BV點數總和。', { autoCloseSeconds: 5 });
      }
    },
    watch: {
      mainDate: {
        handler(newValue) {
          console.log('StatisticsCom date changed:', newValue);
          this.date = newValue;
        },
        deep: true
      },
      date: {
        handler(newValue) {
          this.$emit('update:main-date', newValue);
        },
        deep: true
      }
    },
  };
  </script>
  
  <style lang="scss" scoped>

  $spacer: 0.875rem;
  $headerHeight: calc($spacer * 3);
  $primary: #00a8ca;

  .h-header {
    height: $headerHeight;
  }
  main {
    margin: calc($headerHeight * 2) 0 69.5px;
    &.hideTitle {margin: $headerHeight 0 69.5px;}
  }
  .list-group-item {
    border: 0;
  }
  .pie-chart {
    height: 13rem;
    width: 100%;
    max-width: 13rem;
    margin: 0 auto;
  }
  .ranking-dot {
    width: 0.875rem;
    height: 0.875rem;
  }
  .mw-20rem {
    max-width: 20rem;
  }
  .close-icon {
    cursor: pointer;
    height: 100%; width: 2rem;
    display: flex;
    align-items: center; justify-content: center;
  }
  .info-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1rem;
    height: 1rem;
    border: 1px solid #999;
    border-radius: 50%;
    font-size: 0.75rem;
    color: #999;
    cursor: pointer;
    user-select: none;
    line-height: 1;
  }
  .beta-badge {
    font-size: 0.6rem;
    padding: 1px 3px;
    background-color: #ffba4a;
    color: white;
    border-radius: 3px;
    font-weight: 500;
  }
  </style>
  
  